// src/features/developers/i18n/en.ts

export default {
  name: 'Developers',
  title: 'Developers',
  role: 'Developer',
  intro:
    'Connect your systems to Insight.',
  tabs: {
    label: 'Developer documentation',
    overview: 'Getting started',
    reference: 'Reference',
    recipes: 'Code samples',
    downloads: 'Downloads',
  },
  pills: {
    version: 'v%{version}',
    operations: '%{smart_count} operation |||| %{smart_count} operations',
    thisEnvironment: 'This environment',
  },
  environments: {
    production: 'Production',
    development: 'Test (sandbox)',
  },
  loading: 'Loading…',
  copy: 'Copy',
  copied: 'Copied',
  errors: {
    forbidden: 'Your user cannot read the developer documentation. Ask your administrator or Roommatik for access.',
    unavailable: 'The documentation is not available right now. Getting started and the code samples do not depend on it. Try again later.',
    viewer: 'The reference viewer could not be loaded. Reload the page and try again.',
    download: 'The download did not complete. Try again.',
  },
  pitch: {
    eyebrow: 'Connect your systems',
    title: 'The data of your operation, one step from where you need it',
    text:
      'Insight gathers what happens at the reception of your properties. With this API that data flows both ways: without opening the application and without custom projects.',
    send: {
      title: 'Send data to Insight',
      text:
        'Your reservation system, your locks, your guest app… What happens in them shows up in the Insight indicators, next to what the kiosks already record, with no new screens to learn.',
      points: [
        'Reservations, stays, guest journeys and device status.',
        'Batches of up to 500 events in an open format.',
        'Sending an event again is safe: it is recognised as a duplicate.',
      ],
    },
    read: {
      title: 'Take Insight data to your systems',
      text:
        'The indicators, check-ins and reservations of each property, ready for your analytics tool, your intranet or your revenue system.',
      points: [
        'The same indicators you see in Insight.',
        'Check-ins and reservations per property, with the guest shown as «Juan G.».',
        'Only the properties you authorise.',
      ],
    },
  },
  useCases: {
    title: 'What you can build',
    bi: {
      title: 'Your own dashboard',
      text: 'Bring the indicators of all your properties into your analytics tool and combine them with your sales and occupancy.',
    },
    intranet: {
      title: 'An operations intranet',
      text: "Show your team each property's arrivals and check-ins of the day, without anyone opening another application.",
    },
    revenue: {
      title: 'Revenue and planning',
      text: 'Cross the reservations arriving in each period and the use of self-service with your forecasts and your rates.',
    },
    systems: {
      title: 'Your system, in the indicators',
      text: 'Have your reservation or lock system report to Insight, and see its events in the same indicators as the kiosk.',
    },
  },
  cta: {
    title: 'Do you want to connect your systems to Insight?',
    text: 'Roommatik gives you the access keys after agreeing with you the properties and the data you will send or read.',
    button: 'Request access',
    subject: 'Request for access to the Insight API',
    body:
      'Hello,\n\nWe would like to connect our systems to Insight.\n\nOrganisation:\nProperties:\nWhat we want to do (send data, read data or both):\n\nThank you.',
  },
  quickStart: {
    title: 'Quick start',
    steps: {
      access: {
        title: 'Request your keys',
        text:
          'Roommatik issues the keys: one to read data and one for each system that sends data. They are shown once, so keep them on your server.',
      },
      check: {
        title: 'Check your key',
        text: 'This call returns the properties your key can read. If the answer lists them, you are connected.',
      },
      read: {
        title: 'Read the indicators',
        text: 'Ask for the figures of a period, or for the check-ins or reservations of a property.',
      },
      send: {
        title: 'Send your events',
        text: 'Send batches of events from your system. Try the development environment first.',
      },
    },
    toRecipes: 'See the code samples',
    toReference: 'Open the reference',
  },
  cards: {
    environments: {
      title: 'Environments',
      text:
        'Data and keys are independent in each environment: a key of one does not work in the other. The development environment has test properties: verify your integration there before moving to production.',
    },
    authentication: {
      title: 'Authentication',
      head: ['Header', 'What it is for'],
      rows: [
        ['`X-Api-Key`', 'Read data. The key belongs to your organization and is limited to the properties it was issued for. It starts with `ink_`.'],
        ['`X-Source-Key`', 'Send data. The key belongs to one sender and to the properties it may report for. It starts with `rmk2_`.'],
      ],
      text:
        'Keep the keys on your server, never in a web page or a mobile app. A revoked key stops working at once and gets a 401.',
    },
    requests: {
      title: 'Requests',
      items: [
        'Everything is HTTPS and JSON.',
        'Dates are `YYYY-MM-DD`, in the calendar of the property.',
        'A period is at most 92 days.',
        'Lists are paged with `page` and `pageSize` (up to 200; 50 by default).',
      ],
    },
    responses: {
      title: 'Responses',
      text:
        'Reads answer `success: true` with the result in `data`; lists add `paging`. Errors have the same shape, with a code and a message. Rely on the HTTP status.',
    },
    errors: {
      title: 'Errors',
      head: ['Status', 'What it means'],
      rows: [
        ['`400`', 'A parameter is missing or invalid; the message says which.'],
        ['`401`', 'The key is missing, unknown or revoked.'],
        ['`404`', 'The property does not exist or is not available to your key.'],
        ['`429`', 'Too many requests: wait the seconds given by the `Retry-After` header.'],
      ],
    },
    limits: {
      title: 'Limits',
      items: [
        '60 requests per minute per key; beyond that you get a 429 with `Retry-After`.',
        'Periods of up to 92 days and up to 10 indicators per request.',
        '`pageSize` of up to 200.',
        'Batches of up to 500 events and 2 MB per request.',
      ],
    },
    personalData: {
      title: 'Personal data',
      text:
        'Check-ins and reservations identify the guest only by first name and the initial of the surname («Juan G.»). Documents, e-mail addresses, phone numbers, birth dates and addresses are never returned.',
    },
    assistant: {
      title: 'Work with a coding assistant',
      text:
        'Download the OpenAPI file and give it to your coding assistant or to a client generator: it is the exact contract of the version deployed right now, with an example for every request.',
      button: 'Go to the downloads',
    },
  },
  reference: {
    loading: 'Loading the reference…',
  },
  recipes: {
    intro:
      'The samples point at `%{baseUrl}`, the environment you are looking at, and read the keys from the environment: set `INSIGHT_API_KEY` to read data and `INSIGHT_SOURCE_KEY` to send it before running them.',
    language: 'Language',
    properties: {
      title: 'Find out which properties you can read',
      description:
        'Returns the properties of your key with their id, time zone and country. Use that id in the rest of the calls.',
    },
    indicators: {
      title: 'Read the indicators of a period',
      description:
        'Ask for up to 10 indicators of a property over a period of up to 92 days. Each comes with its value, the sample size and, when it cannot be calculated, the reason. The full catalogue is at `GET /api/v2/data/metrics`. Replace the id with one from the first call.',
    },
    checkins: {
      title: 'Page through the check-ins of a property',
      description:
        'Lists are paged: ask for pages until you have gathered `paging.total` items. The reservations arriving in the period are read the same way at `/reservations`. The guest is shown as «Juan G.».',
    },
    events: {
      title: 'Send a batch of events',
      description:
        'A batch is a list of up to 500 events, sent with the sender key. The answer says, event by event, whether it was stored, was a duplicate or was rejected. Try the development environment first.',
    },
  },
  downloads: {
    postman: {
      title: 'Postman',
      text:
        'Every operation of the reference as a request that runs as downloaded, with the bodies filled in and the keys in variables.',
      collection: 'Download the collection',
      steps: [
        'Import the collection and one environment file into Postman.',
        'Select the environment and set `apiKey` and `sourceKey`.',
        'Open `Read data` and its first request, and press Send.',
      ],
    },
    openapi: {
      title: 'OpenAPI',
      text:
        'The OpenAPI 3 specification of the version deployed right now (v%{version}). Generate a typed client, import it into Insomnia, Bruno or Hoppscotch, or give a coding assistant the exact contract.',
      button: 'Download the OpenAPI file',
      note:
        'It is checked against the implementation on every build: a route that exists without being documented, or documented without existing, breaks the build. It does not drift from what is deployed.',
    },
  },
  keys: {
    open: 'Access keys',
    back: 'Back to Developers',
    title: 'Access keys',
    subtitle: 'The keys that let a customer read the data of its properties. Only Roommatik staff issue and revoke them.',
    empty: 'No key has been issued yet.',
    create: 'Create key',
    columns: {
      name: 'Name',
      owner: 'Owner',
      properties: 'Properties',
      key: 'Key',
      createdBy: 'Created by',
      created: 'Created',
      lastUsed: 'Last used',
      status: 'Status',
    },
    status: { active: 'Active', revoked: 'Revoked' },
    neverUsed: 'Never used',
    propertiesCount: '%{smart_count} property |||| %{smart_count} properties',
    revoke: 'Revoke',
    form: {
      title: 'New access key',
      name: 'Key name',
      nameHelp: 'For example, «ERP integration».',
      owner: 'Owner organization',
      ownerHelp: 'The organization that will use the key.',
      properties: 'Properties',
      propertiesHelp: 'The key will only read these properties.',
      selectAll: 'Select all',
      noProperties: 'There are no properties available.',
      cancel: 'Cancel',
      submit: 'Create key',
      invalid: {
        name: 'Enter a name.',
        nameTooLong: 'The name accepts up to 120 characters.',
        owner: 'Enter the owner organization.',
        ownerTooLong: 'The organization accepts up to 200 characters.',
        properties: 'Select at least one property.',
      },
    },
    failures: {
      INVALID_NAME: 'The name is not valid.',
      INVALID_OWNER: 'The owner organization is not valid.',
      INVALID_PROPERTIES: 'The selected properties are not valid.',
      UNKNOWN_PROPERTIES: 'One of the properties is not available in this environment.',
      UNKNOWN_ORGANIZATION: 'The organization does not exist.',
      generic: 'The key could not be created. Try again.',
    },
    created: {
      title: 'Key created',
      warning: 'Save it now: it will not be shown again.',
      text: 'Give this key to its owner through a secure channel. Roommatik will not be able to show it again.',
      copy: 'Copy key',
      copied: 'Copied',
      close: 'Close',
    },
    revokeDialog: {
      title: 'Revoke the key «%{name}»',
      text: 'The key stops working at once and the integration using it will receive 401 errors. This cannot be undone.',
      confirm: 'Revoke key',
      cancel: 'Cancel',
      failed: 'The key could not be revoked. Try again.',
    },
  },
};
